import { AfterViewInit, Component, ElementRef, HostListener, NgZone, QueryList, ViewChild, ViewChildren } from '@angular/core';

import { faLinkedinIn, faGithub, faTwitter, faInstagram, faFacebookF } from '@fortawesome/free-brands-svg-icons';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { TranslateService } from '@ngx-translate/core';

import { GsapService } from '../animations/gsap.service';

@Component({
  selector: 'placeme-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
})
export class LayoutComponent implements AfterViewInit {
  faFacebookF = faFacebookF;
  faGithub = faGithub;
  faInstagram = faInstagram;
  faLinkedinIn = faLinkedinIn;
  faTwitter = faTwitter;
  header: HTMLElement;
  langs = ['fr', 'en'];
  menu: HTMLElement;
  menuItems: HTMLElement[];
  nav: HTMLElement;

  @ViewChild('headerRef', { static: false }) set headerRef(headerRef: ElementRef<HTMLElement>) {
    this.header = headerRef.nativeElement;
  }
  @ViewChild('menuRef', { static: false }) set menuRef(menuRef: ElementRef<HTMLElement>) {
    this.menu = menuRef.nativeElement;
  }
  @ViewChildren('menuItemRef') set menuItemsRef(menuItemsRef: QueryList<ElementRef<HTMLElement>>) {
    this.menuItems = menuItemsRef.map(elm => elm.nativeElement);
  }
  @ViewChild('menuListRef', { static: false }) set navRef(navRef: ElementRef<HTMLElement>) {
    this.nav = navRef.nativeElement;
  }

  public get isActiveMenu(): boolean {
    return this._activeMenu;
  }
  private _activeMenu = false;

  public get isAnimating(): boolean {
    return this._animating;
  }
  private _animating: boolean;

  constructor(
    private localizeRouterService: LocalizeRouterService,
    private translateService: TranslateService,
    private ngZone: NgZone,
    private gsapService: GsapService
  ) {}

  activate(): gsap.core.Timeline {
    return this.gsapService
      .timeline()
      .fromTo(
        this.menu,
        {
          opacity: 0,
          pointerEvents: 'none',
          clipPath: 'ellipse(30% 20% at 50% 0%)',
        },
        {
          opacity: 1,
          duration: 0.3,
          display: 'flex',
          clipPath: 'ellipse(150% 100% at 50% 10%)',
          pointerEvents: 'all',
        }
      )
      .fromTo(
        this.nav,
        {
          clipPath: 'polygon(0px 0px, 100% 0px, 100% 10%, 0 100%)',
          height: '0%',
        },
        {
          height: '90%',
          clipPath: 'polygon(0px 0px, 100% 0px, 100% 100%, 0 100%)',
          ease: this.gsapService.power2().easeOut,
        },
        '-=0.2'
      )
      .fromTo(
        this.menuItems.map(elm => elm.children),
        {
          y: '100%',
          opacity: 0,
        },
        {
          duration: 1.2,
          ease: this.gsapService.expo().easeOut,
          y: 0,
          opacity: 1,
          stagger: 0.06,
        },
        '-=0.15'
      );
  }

  inactivate(): gsap.core.Timeline {
    return this.gsapService
      .timeline()
      .to(
        this.menuItems.map(elm => elm.children),
        {
          y: '100%',
          opacity: 0,
        }
      )
      .to(
        this.nav,
        {
          clipPath: 'polygon(0px 0px, 100% 0px, 100% 100%, 0 10%)',
          height: '0%',
          ease: this.gsapService.power2().easeIn,
        },
        '-=0.5'
      )
      .to(this.menu, {
        opacity: 0,
        pointerEvents: 'none',
        duration: 0.1,
      });
  }

  isCurrentLang(lang: string): boolean {
    return this.localizeRouterService.parser.currentLang === lang;
  }

  ngAfterViewInit(): void {
    this.setState();
    this.gsapService.createScrollTrigger({
      start: 'top -80',
      end: 99999,
      toggleClass: { className: 'toolbar--scrolled', targets: this.header },
    });
  }

  @HostListener('mousedown', ['$event'])
  onClick(event: any): void {
    if (event.target.classList.contains('menu')) {
      this.toggleMenu();
    }
  }

  setLang(lang: string): void {
    this.localizeRouterService.changeLanguage(lang);
    this.translateService.use(lang);
  }

  toggleMenu(): void {
    this._animating = true;
    const animation: gsap.core.Timeline = this.isActiveMenu ? this.inactivate() : this.activate();
    this._activeMenu = !this.isActiveMenu;

    animation.add(() => {
      this.ngZone.run(() => (this._animating = false));
    });
  }

  private setState(): void {
    if (this.isActiveMenu) {
      this.gsapService.set(this.menu, { display: 'flex' });
    } else {
      this.gsapService.set(this.menu, { display: 'none' });
    }
  }
}
